import { Tab, Tabs } from '@mui/material';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { Divider } from 'src/components/shared/Divider/Divider';
import { TabPanel } from 'src/components/shared/Tabs/TabPanel';
import { useReduxDispatch } from 'src/store';
import { alertError, alertSuccess } from 'src/store/alerts/alerts.actions';
import { getRequest, postRequest, putRequest } from 'src/store/api-handler';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { ProductDetailDto } from 'src/store/products/product';
import {
  mapProductDetailToEdit,
  mapProductParameterToEdit,
  mapProductProductToEdit
} from 'src/store/products/product.map';
import { ProductParameterDetailDto } from 'src/store/products/productParameter';
import { ProductProductDto } from 'src/store/products/productProduct';
import {
  ProductTypes,
  createProduct,
  editProduct,
  productActions
} from 'src/store/products/products.actions';
import { ProductApi } from 'src/store/products/products.api';
import { selectProductsRequestState } from 'src/store/products/products.selectors';
import { selectRunSystems } from 'src/store/runsystems/runsystems.selectors';
import {
  SpecificationDto,
  SpecificationParameterDto
} from 'src/store/specifications/specification';
import { download, getFileNameFromHeader } from 'src/utils/download';
import { push } from 'src/utils/navigation';
import { Analyzes } from './Analyzes';
import { Collections } from './Collections';
import { Logs } from './Logs';
import { Norlab } from './Norlab';
import { ProductDetailsForm } from './ProductDetailsForm';

export const ProductPage = () => {
  const [tab, setTab] = useState<number>(0);
  const grades = useSelector(selectActiveGrades);
  const runSystems = useSelector(selectRunSystems);

  const [product, setProduct] = useState<Nullable<ProductDetailDto>>(null);

  const [specification, setSpecification] =
    useState<Nullable<SpecificationDto>>(null);

  const [specifications, setSpecifications] = useState<SpecificationDto[]>([]);

  const [productParameters, setProductParameters] = useState<
    ProductParameterDetailDto[]
  >([]);

  const [specificationParameters, setSpecificationParameters] = useState<
    SpecificationParameterDto[]
  >([]);
  const requestState = useSelector(selectProductsRequestState);

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const { id: idParam } = useParams();
  const navigate = useNavigate();

  const fetchProduct = async (id: number) => {
    const response = await ProductApi.getById(id);
    if (response.ok) {
      setProduct(response.data);
    }
  };

  const fetchProductParameters = async (id: number) => {
    const { data } = await getRequest<ProductParameterDetailDto[]>(
      `/products/${id}/parameters`
    );
    setProductParameters(data);
  };

  useEffect(() => {
    if (!!idParam) {
      fetchProduct(Number(idParam));
    }
  }, [idParam, dispatch]);

  useEffect(() => {
    if (product) {
      const fetchSpecifications = async () => {
        const { data } = await getRequest<SpecificationDto[]>(
          `/specifications/grades/${product?.gradeId}`
        );
        setSpecifications(data.filter(s => s.active));
      };

      fetchSpecifications();
      fetchProductParameters(product.id);

      if (product.specificationId != specification?.id) {
        onSpecificationChange(product.specification);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const onCancel = () => {
    push('/products', navigate, RouteAnimation.SlideLeft);
  };

  const onSubmit = async (product: ProductDetailDto) => {
    const dto = mapProductDetailToEdit(product);
    const result = dto.id
      ? await dispatch(editProduct(dto.id, dto))
      : await dispatch(createProduct(dto));
    if (
      result.type === ProductTypes.Add ||
      result.type === ProductTypes.Update
    ) {
      navigate(`/products/${result.product.id}`, {
        replace: true
      });
    }
  };

  const onCreateLot = async (id: number) => {
    const { ok, data, error } = await ProductApi.createLot(id);
    if (ok) {
      dispatch(alertSuccess(t('product.lotCreated')));
      setProduct({ ...product!, lotNo: data, lotDate: new Date() });
    } else {
      dispatch(alertError(error?.errors?.detail as string));
    }
  };

  const onCreateNorlabNo = async (id: number) => {
    const { ok, data, error } = await ProductApi.createNorlabNo(id);
    if (ok) {
      dispatch(alertSuccess(t('product.norlabCreated')));
      setProduct({ ...product!, norlabNo: data, norlabDate: new Date() });
    } else {
      dispatch(alertError(error?.errors?.detail as string));
    }
  };

  const onReadyForMixture = async (productId: number) => {
    const { ok, error } = await postRequest(`/mixturecandidates/${productId}`);

    if (ok) {
      dispatch(alertSuccess(t('product.readyForMixture')));
    } else {
      dispatch(alertError(error?.errors?.detail as string));
    }
  };

  const onSpecificationChange = useCallback(
    async (specification: Nullable<SpecificationDto>) => {
      setSpecification(specification);

      if (specification) {
        const { data } = await getRequest<SpecificationParameterDto[]>(
          `/specifications/${specification.id}/parameters`
        );
        setSpecificationParameters(data);
      } else {
        setSpecificationParameters([]);
      }
    },
    []
  );

  const onEditSpecification = async (specification: SpecificationDto) => {
    if (product && specification) {
      const dto = mapProductDetailToEdit(product);
      dto.specificationId = specification.id;
      dto.packagingId = specification.packagingId;
      dto.palletId = specification.palletId;
      await dispatch(editProduct(product.id, dto));
      await fetchProduct(product.id);
    }
  };

  const onSaveProductParameters = async (
    parameters: ProductParameterDetailDto[]
  ) => {
    if (product) {
      const dtos = parameters.map(p => {
        const formattedP = { ...p };
        if (
          formattedP.measuredValue !== undefined &&
          formattedP.measuredValue !== null
        ) {
          formattedP.measuredValue = parseFloat(
            formattedP.measuredValue.toFixed(4)
          );
        }
        return mapProductParameterToEdit(product, formattedP);
      });
      const { ok, error } = await putRequest(
        `/products/${product.id}/parameters`,
        dtos
      );
      batch(() => {
        if (ok) {
          fetchProductParameters(product.id);
          dispatch(alertSuccess(`${t('parameter.plural')} ${t('save.past')}`));
        } else {
          dispatch(alertError(error?.errors?.detail as string));
        }
      });
    }
  };

  const onSortChange = useCallback(
    (event: GridSortChangeEvent) => {
      if (product) {
        dispatch(
          productActions.updateRequestState({
            ...requestState,
            sort: event.sort
          })
        );
      }
    },
    [dispatch, requestState, product]
  );

  const onFilterChange = useCallback(
    (filter: CompositeFilterDescriptor | undefined) => {
      if (product) {
        dispatch(
          productActions.updateRequestState({
            ...requestState,
            filter: filter,
            skip: 0
          })
        );
      }
    },
    [dispatch, requestState, product]
  );

  const onResetFilters = useCallback(() => {
    if (product) {
      dispatch(
        productActions.updateRequestState({
          ...requestState,
          filter: undefined,
          skip: 0
        })
      );
    }
  }, [dispatch, requestState, product]);

  const onPageChange = useCallback(
    (skip: number, take: number) => {
      if (product) {
        dispatch(
          productActions.updateRequestState({
            ...requestState,
            skip,
            take
          })
        );
      }
    },
    [dispatch, requestState, product]
  );

  const onSaveHasProducts = async (
    productProducts: ProductProductDto[]
    // allProdcuts = Dataresult
  ) => {
    // Ensure that the product is not null
    if (product) {
      const dtos = productProducts.map(pp =>
        mapProductProductToEdit(product, pp)
      );
      const { ok, error } = await putRequest(
        `/products/${product.id}/products`,
        dtos
      );
      // Using batch to group dispatches and state updates together.
      // This ensures that all updates are applied at once, and the components won't
      // try to read the state while it's still being updated.
      batch(() => {
        if (ok) {
          setProduct({ ...product, hasProducts: productProducts });
          dispatch(
            alertSuccess(`${t('product.collection')} ${t('save.past')}`)
          );
        } else {
          dispatch(alertError(error?.errors?.detail as string));
        }
      });
    } else {
      console.error('Product is null');
    }
  };

  const onDownloadDeliveryNote = async () => {
    axios({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `/products/${product?.id}/deliverynote`
    })
      .then(response => {
        const disposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];
        const filename = getFileNameFromHeader(disposition);
        const blob = new Blob([response.data as Blob], {
          type: contentType
        });
        download(filename, blob);
      })
      .catch(() => {});
  };

  const onDownloadProductData = async () => {
    axios({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `/products/${product?.id}/productdata`
    })
      .then(response => {
        const disposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];
        const filename = getFileNameFromHeader(disposition);
        const blob = new Blob([response.data as Blob], {
          type: contentType
        });
        download(filename, blob);
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <ProductDetailsForm
        product={product}
        specification={specification}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onCreateLot={onCreateLot}
        onReadyForMixture={onReadyForMixture}
        onSpecificationChange={() => onSpecificationChange(null)}
        onDownloadDeliveryNote={onDownloadDeliveryNote}
        onDownloadProductData={onDownloadProductData}
      />
      <Divider />
      <Tabs
        value={tab}
        onChange={(_, tab) => setTab(tab)}
        aria-label="Product tabs"
      >
        <Tab label={t('analysis.plural')} />
        <Tab label={t('product.logs')} />
        <Tab label={t('product.norlab')} />
        <Tab label={t('product.collection')} />
      </Tabs>
      <TabPanel index={0} value={tab}>
        <Analyzes
          product={product}
          productParameters={productParameters}
          specification={specification}
          specifications={specifications}
          specificationParameters={specificationParameters}
          onSpecificationChange={onSpecificationChange}
          onEditSpecification={onEditSpecification}
          onSaveProductParameters={onSaveProductParameters}
        />
      </TabPanel>
      <TabPanel index={1} value={tab}>
        <Logs product={product} />
      </TabPanel>
      <TabPanel index={2} value={tab}>
        <Norlab product={product} onCreateNorlabNo={onCreateNorlabNo} />
      </TabPanel>
      <TabPanel index={3} value={tab}>
        <Collections
          product={product}
          grades={grades}
          runSystems={runSystems}
          requestState={requestState}
          onSaveHasProducts={onSaveHasProducts}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
          onSortChange={onSortChange}
          onPageChange={onPageChange}
        />
      </TabPanel>
    </React.Fragment>
  );
};
